import { ff } from '@atlassian/jira-feature-flagging';
import type { ViewIssueData as IssueServiceViewIssueData } from '@atlassian/jira-issue-fetch-services/src/types';
import type { CommentsTransformResult } from '@atlassian/jira-issue-gira-transformer-types';
import { NUM_INITIAL_ITEMS_TO_LOAD } from '@atlassian/jira-issue-view-common-constants/src/activity-feed';
import type { ViewIssueData } from '@atlassian/jira-issue-view-common-types/src/gira';
import { isOutsiderCommentFFEnabled } from '../../../feature-flags';
import {
	transformComments,
	normalizeComments,
	normalizeGiraComments,
} from '../../comment-transformer';

import { getCommentsQuery } from './graphql';

const CREATED_ORDER_DESCENDING = '-created';

export const getQuery = () =>
	getCommentsQuery(
		NUM_INITIAL_ITEMS_TO_LOAD,
		0,
		CREATED_ORDER_DESCENDING,
		isOutsiderCommentFFEnabled(),
	);

export const transformData = (
	issue: ViewIssueData | IssueServiceViewIssueData,
): CommentsTransformResult => {
	const { comments } = issue;

	if (!comments) {
		return {
			// eslint-disable-next-line @typescript-eslint/consistent-type-assertions, @typescript-eslint/no-explicit-any
			users: {} as any,
			// eslint-disable-next-line @typescript-eslint/consistent-type-assertions, @typescript-eslint/no-explicit-any
			comments: {} as any,
			totalComments: 0,
			commentsStartIndex: 0,
			loadedComments: 0,
		};
	}

	const { totalCount: totalComments, startIndex: commentsStartIndex } = comments;

	const response = {
		data: {
			issueComments: comments,
		},
	};

	return {
		...normalizeComments(transformComments(response), 0, commentsStartIndex),
		totalComments,
		commentsStartIndex,
	};
};

export const transformGiraData = (
	issue: ViewIssueData | null,
	loadedComments: number,
	commentsStartIndex: number,
	type?: string,
	orderBy?: string,
): CommentsTransformResult => {
	const emptyComments = {
		users: {},
		comments: {},
		totalComments: 0,
		commentsStartIndex: 0,
		loadedComments: 0,
	};

	if (ff('catch-issue-comments-empty-response-errors_o2gkc') && issue?.comments == null) {
		return emptyComments;
	}

	// TODO: Remove below lines when cleaning up catch-issue-comments-empty-response-errors_o2gkc FF as above condition covers both cases
	// @ts-expect-error - Property 'comments' does not exist on type 'ViewIssueData | null'
	const { comments } = issue;
	if (!comments) {
		return emptyComments;
	}

	const { totalCount: totalComments } = comments;

	const response = {
		data: {
			issueComments: comments,
		},
	};

	return {
		...normalizeGiraComments(
			transformComments(response),
			loadedComments,
			commentsStartIndex,
			type,
			orderBy,
		),
		totalComments,
	};
};
